// A guest ticket code: the numbers every surface has to agree on (spec/05 §3.4, spec/12 §3.2, F-SES-07, ADR-0059).
//
// "A 6-character ticket code (unambiguous alphabet, no 0/O/1/I)". The counter shows it after the sale is paid, the
// receipt prints it, the guest types it at a PC's "I have a ticket", and a player types it to claim it. The server
// mints it from this alphabet and enforces the limits; the kiosk and the player web check its shape with the same
// pattern before they send it.
import { z } from "../zod.js";
/** The characters a ticket code is made of: A–Z and 2–9 without the look-alikes 0, O, 1 and I. */
export const GUEST_TICKET_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
/** Characters in a ticket code. */
export const GUEST_TICKET_CODE_LENGTH = 6;
/** A ticket code, upper case: six characters of `GUEST_TICKET_ALPHABET`. */
export const GUEST_TICKET_CODE_PATTERN = /^[A-HJ-NP-Z2-9]{6}$/;
/** How long after it is paid a guest ticket can be claimed into an account (spec/05 §3.4: "within 7 days"). */
export const GUEST_CLAIM_WINDOW_DAYS = 7;
/** Wrong ticket codes a PC takes in `GUEST_TICKET_LOCKOUT_SECONDS` before it takes none for that long. */
export const GUEST_TICKET_MAX_WRONG = 5;
/** The window wrong codes are counted in, and how long a PC that reached the limit takes no code. */
export const GUEST_TICKET_LOCKOUT_SECONDS = 15 * 60;
/** Read a typed code the way every surface does: spaces and dashes dropped, upper-cased. */
export function normaliseGuestTicketCode(typed) {
    return typed.replace(/[\s-]+/g, '').toUpperCase();
}
/** A ticket code as the server answers it: exactly six characters of the alphabet, upper case. */
export const GuestTicketCodeSchema = z
    .string()
    .regex(GUEST_TICKET_CODE_PATTERN, 'Expected the 6-character ticket code')
    .openapi('GuestTicketCode', {
    description: 'A guest ticket code: six characters from A–Z and 2–9 without 0, O, 1 and I.',
    example: 'K7PX2M',
});
/**
 * A ticket code as a person typed it: the server reads it with `normaliseGuestTicketCode` (spaces and dashes dropped,
 * upper-cased) and answers anything that is then not a code as a wrong code, counted like one.
 */
export const TypedGuestTicketCodeSchema = z
    .string()
    .min(1)
    .max(16)
    .openapi('TypedGuestTicketCode', { description: 'A guest ticket code as typed: spaces and dashes are ignored, lower case is read as upper.', example: 'k7px-2m' });
//# sourceMappingURL=guest-ticket.js.map